import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Loader2, ArrowRight, ArrowLeft, Check, Languages } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import AnimatedGenerateButton from '@/components/ui/animated-generate-button-shadcn-tailwind';
import { useLanguage } from '@/components/LanguageProvider';
import { useOnboardingFlow } from '@/features/onboarding/OnboardingFlowProvider';
import { submitOnboardingSetup } from '@/lib/api/onboarding';
import { useSession } from '@/lib/hooks/useSession';
import { cn } from '@/lib/utils';
import type { SupportedLanguage } from '@/lib/i18n/config';
import type {
  OnboardingConceptType,
  OnboardingOrderingChannel,
  OnboardingPrepStyle,
  OnboardingSetupForm,
} from '@/types/onboarding';

type StepId =
  | 'venueName'
  | 'conceptType'
  | 'seatingIndoor'
  | 'seatingPatio'
  | 'seatingBar'
  | 'orderingChannels'
  | 'prepStyle'
  | 'notes';

interface StepDefinition {
  id: StepId;
  requiresValidation?: boolean;
  validate?: (form: OnboardingSetupForm) => boolean;
}

const TOTAL_TIMER_MS = 7 * 60 * 1000;

const steps: StepDefinition[] = [
  {
    id: 'venueName',
    requiresValidation: true,
    validate: (form) => form.venueName.trim().length >= 2,
  },
  {
    id: 'conceptType',
    requiresValidation: true,
    validate: (form) => Boolean(form.conceptType),
  },
  {
    id: 'seatingIndoor',
    requiresValidation: true,
    validate: (form) => form.seating.indoor >= 0,
  },
  {
    id: 'seatingPatio',
    requiresValidation: true,
    validate: (form) => form.seating.patio >= 0,
  },
  {
    id: 'seatingBar',
    requiresValidation: true,
    validate: (form) => form.seating.bar >= 0,
  },
  {
    id: 'orderingChannels',
    requiresValidation: true,
    validate: (form) => form.orderingChannels.length > 0,
  },
  {
    id: 'prepStyle',
    requiresValidation: true,
    validate: (form) => Boolean(form.prepStyle),
  },
  {
    id: 'notes',
  },
];

const ensureWithinBounds = (value: number, min: number, max: number): number => {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
};

const QuestionnairePage = () => {
  const { t } = useTranslation(['onboarding', 'common']);
  const navigate = useNavigate();
  const flow = useOnboardingFlow();
  const session = useSession();
  const { availableLanguages, resolvedLanguage, setLanguage } = useLanguage();

  const [activeIndex, setActiveIndex] = useState(0);
  const [form, setForm] = useState<OnboardingSetupForm>(() => {
    if (flow.questionnaire) {
      return {
        ...flow.questionnaire,
        notes: flow.questionnaire.notes ?? '',
      };
    }
    return {
      venueName: flow.tenantName ?? '',
      conceptType: 'brick_and_mortar',
      seating: {
        indoor: 10,
        patio: 4,
        bar: 0,
      },
      orderingChannels: ['qr'],
      prepStyle: 'fast_casual',
      notes: '',
    } satisfies OnboardingSetupForm;
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const initialSyncRef = useRef(false);
  const [allowSignupRedirect, setAllowSignupRedirect] = useState(false);
  const [allowPaymentRedirect, setAllowPaymentRedirect] = useState(false);
  const [showLaunchScreen, setShowLaunchScreen] = useState(false);
  const [launchButtonActive, setLaunchButtonActive] = useState(false);
  const launchStartedRef = useRef(false);
  const launchTimeoutRef = useRef<number | null>(null);
  const latestFormRef = useRef(form);
  useEffect(() => {
    latestFormRef.current = form;
  }, [form]);
  const languageOptions = useMemo(
    () => availableLanguages.map((code) => ({ code, label: code.toUpperCase() })),
    [availableLanguages],
  );
  const handleLanguageSelect = useCallback(
    (code: SupportedLanguage) => {
      if (code === resolvedLanguage) {
        return;
      }
      setLanguage(code);
    },
    [resolvedLanguage, setLanguage],
  );

  useEffect(() => {
    if (!flow.hydrated || session.loading) {
      setAllowSignupRedirect(false);
      return;
    }

    if (!allowSignupRedirect) {
      setAllowSignupRedirect(true);
      return;
    }

    if (!flow.hasSignupContext) {
      navigate('/auth/signup', { replace: true });
    }
  }, [
    allowSignupRedirect,
    flow.hydrated,
    flow.hasSignupContext,
    navigate,
    session.loading,
  ]);

  useEffect(() => {
    if (!flow.hydrated || session.loading) {
      setAllowPaymentRedirect(false);
      return;
    }

    if (!allowPaymentRedirect) {
      setAllowPaymentRedirect(true);
      return;
    }

    if (!flow.paymentComplete) {
      navigate('/onboarding/payment', { replace: true });
    }
  }, [
    allowPaymentRedirect,
    flow.hydrated,
    flow.paymentComplete,
    navigate,
    session.loading,
  ]);

  useEffect(() => {
    const start = Date.now();
    const timer = window.setInterval(() => {
      const delta = Date.now() - start;
      setElapsedMs((prev) => {
        if (prev >= TOTAL_TIMER_MS) {
          return prev;
        }
        return Math.min(TOTAL_TIMER_MS, delta);
      });
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const syncForm = useCallback(
    (updater: (previous: OnboardingSetupForm) => OnboardingSetupForm) => {
      setForm((previous) => {
        const next = updater(previous);
        flow.saveQuestionnaire(next);
        return next;
      });
    },
    [flow],
  );

  useEffect(() => {
    if (!initialSyncRef.current) {
      initialSyncRef.current = true;
      flow.saveQuestionnaire(form);
    }
  }, [flow, form]);

  const mutation = useMutation({
    mutationFn: submitOnboardingSetup,
    onMutate: () => {
      setSubmitError(null);
      setPreparing(true);
    },
    onSuccess: (result) => {
      const destination = result.nextPath || `/r/${flow.tenantSlug ?? ''}/dashboard`;
      const finalizeNavigation = () => {
        flow.reset();
        navigate(destination, { replace: true });
      };

      window.setTimeout(() => {
        const refreshSessionAndNavigate = async () => {
          try {
            const nextSession = await session.triggerRefetch();
            if (
              import.meta.env.DEV &&
              nextSession.onboardingStatus !== 'completed'
            ) {
              console.warn(
                '[OnboardingQuestionnaire] session refetch did not report completed onboarding status',
                nextSession,
              );
            }
          } catch (error) {
            if (import.meta.env.DEV) {
              console.warn(
                '[OnboardingQuestionnaire] failed to refetch session after onboarding setup',
                error,
              );
            }
          } finally {
            finalizeNavigation();
          }
        };

        void refreshSessionAndNavigate();
      }, 1200);
    },
    onError: (error: unknown) => {
      setPreparing(false);
      if (launchTimeoutRef.current !== null) {
        window.clearTimeout(launchTimeoutRef.current);
        launchTimeoutRef.current = null;
      }
      launchStartedRef.current = false;
      setLaunchButtonActive(false);
      setSubmitError(error instanceof Error ? error.message : t('onboarding:questionnaire.errors.submit', { defaultValue: 'We could not finalize your setup. Please try again.' }));
    },
  });
  const { mutate, isPending } = mutation;

  const currentStep = steps[activeIndex];
  const progressValue = useMemo(() => {
    return Math.round(((activeIndex + 1) / steps.length) * 100);
  }, [activeIndex]);

  const remainingMs = Math.max(TOTAL_TIMER_MS - elapsedMs, 0);
  const totalSeconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');

  const canAdvance = useMemo(() => {
    if (!currentStep || !currentStep.requiresValidation || !currentStep.validate) {
      return true;
    }
    return currentStep.validate(form);
  }, [currentStep, form]);

  const canSubmit = useMemo(() => {
    const seatingCounts = [form.seating.indoor, form.seating.patio, form.seating.bar];
    const hasValidSeatingInputs = seatingCounts.every((count) => Number.isFinite(count) && count >= 0);

    return hasValidSeatingInputs && form.orderingChannels.length > 0 && form.venueName.trim().length >= 2;
  }, [form]);

  const handleNext = () => {
    if (showLaunchScreen || isPending) {
      return;
    }
    if (isLastStep) {
      if (!canSubmit) {
        return;
      }
      setShowLaunchScreen(true);
      return;
    }
    if (!canAdvance) {
      return;
    }
    setActiveIndex((index) => Math.min(index + 1, steps.length - 1));
  };

  const handleBack = () => {
    if (showLaunchScreen) {
      if (launchTimeoutRef.current !== null) {
        window.clearTimeout(launchTimeoutRef.current);
        launchTimeoutRef.current = null;
      }
      setShowLaunchScreen(false);
      setLaunchButtonActive(false);
      launchStartedRef.current = false;
      return;
    }
    setActiveIndex((index) => Math.max(index - 1, 0));
  };

  const toggleConceptType = (conceptType: OnboardingConceptType) => {
    syncForm((previous) => ({
      ...previous,
      conceptType,
    }));
  };

  const updateSeating = (key: 'indoor' | 'patio' | 'bar', value: string) => {
    const numeric = ensureWithinBounds(parseInt(value, 10), 0, 200);
    syncForm((previous) => ({
      ...previous,
      seating: {
        ...previous.seating,
        [key]: numeric,
      },
    }));
  };

  const toggleChannel = (channel: OnboardingOrderingChannel) => {
    syncForm((previous) => {
      const exists = previous.orderingChannels.includes(channel);
      if (exists) {
        const nextChannels = previous.orderingChannels.filter((item) => item !== channel);
        return {
          ...previous,
          orderingChannels: nextChannels.length > 0 ? nextChannels : previous.orderingChannels,
        };
      }
      return {
        ...previous,
        orderingChannels: [...previous.orderingChannels, channel],
      };
    });
  };

  const setPrepStyle = (prepStyle: OnboardingPrepStyle) => {
    syncForm((previous) => ({
      ...previous,
      prepStyle,
    }));
  };

  const setVenueName = (value: string) => {
    syncForm((previous) => ({
      ...previous,
      venueName: value,
    }));
  };

  const setNotes = (value: string) => {
    syncForm((previous) => ({
      ...previous,
      notes: value,
    }));
  };

  useEffect(() => {
    if (!showLaunchScreen) {
      if (launchTimeoutRef.current !== null) {
        window.clearTimeout(launchTimeoutRef.current);
        launchTimeoutRef.current = null;
      }
      launchStartedRef.current = false;
      setLaunchButtonActive(false);
      return;
    }
    if (!canSubmit || isPending || launchStartedRef.current) {
      return;
    }
    launchStartedRef.current = true;
    setLaunchButtonActive(true);
    launchTimeoutRef.current = window.setTimeout(() => {
      launchTimeoutRef.current = null;
      mutate(latestFormRef.current);
    }, 3500);
    return () => {
      if (launchTimeoutRef.current !== null) {
        window.clearTimeout(launchTimeoutRef.current);
        launchTimeoutRef.current = null;
      }
    };
  }, [showLaunchScreen, canSubmit, isPending, mutate]);

  const handleEnterAdvance = (
    event: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }
    if (currentStep?.id === 'notes') {
      return;
    }
    event.preventDefault();
    if (activeIndex < steps.length - 1) {
      if (canAdvance) {
        handleNext();
      }
      return;
    }
    handleNext();
  };

  const inlineInputClass =
    'h-auto w-full rounded-none border-0 border-b border-border/30 bg-transparent px-0 pb-3 text-lg font-medium text-foreground shadow-none transition-colors placeholder:text-muted-foreground/50 focus-visible:border-foreground focus-visible:bg-transparent focus-visible:pb-3 focus-visible:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0';
  const inlineTextareaClass =
    'min-h-[96px] w-full resize-y rounded-none border-0 border-b border-border/30 bg-transparent px-0 pb-2 text-base text-foreground shadow-none transition-colors placeholder:text-muted-foreground/50 focus-visible:border-foreground focus-visible:bg-transparent focus-visible:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0';

  const renderConceptTypeOptions = () => {
    const options: Array<{ id: OnboardingConceptType; titleKey: string; descriptionKey: string }> = [
      {
        id: 'brick_and_mortar',
        titleKey: 'onboarding:questionnaire.conceptType.options.brick.title',
        descriptionKey: 'onboarding:questionnaire.conceptType.options.brick.description',
      },
      {
        id: 'truck',
        titleKey: 'onboarding:questionnaire.conceptType.options.truck.title',
        descriptionKey: 'onboarding:questionnaire.conceptType.options.truck.description',
      },
      {
        id: 'ghost',
        titleKey: 'onboarding:questionnaire.conceptType.options.ghost.title',
        descriptionKey: 'onboarding:questionnaire.conceptType.options.ghost.description',
      },
    ];

    return (
      <div className="space-y-2">
        {options.map((option) => {
          const selected = form.conceptType === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => toggleConceptType(option.id)}
              className={cn(
                'group flex w-full items-start justify-between border-b border-border/40 py-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40 focus-visible:ring-offset-0',
                selected ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span className="space-y-2">
                <span className="block text-lg font-medium">
                  {t(option.titleKey, {
                    defaultValue:
                      option.id === 'brick_and_mortar'
                        ? 'Full-service restaurant'
                        : option.id === 'truck'
                          ? 'Food truck / mobile'
                          : 'Ghost / virtual kitchen',
                  })}
                </span>
                <span className="block text-sm text-muted-foreground">
                  {t(option.descriptionKey, {
                    defaultValue:
                      option.id === 'brick_and_mortar'
                        ? 'Dining room seating with indoor service workflows.'
                        : option.id === 'truck'
                          ? 'Compact routing tailored to pop-up footprints and lots.'
                          : 'Delivery-first operations with staging and pack-out flows.',
                  })}
                </span>
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  'mt-2 h-3 w-3 rounded-full border border-border/70 transition group-hover:border-foreground/70',
                  selected ? 'border-foreground bg-foreground' : undefined,
                )}
              />
            </button>
          );
        })}
      </div>
    );
  };

  const renderOrderingChannels = () => {
    const channels: Array<{ id: OnboardingOrderingChannel; titleKey: string; descriptionKey: string }> = [
      {
        id: 'qr',
        titleKey: 'onboarding:questionnaire.orderingChannels.qr.title',
        descriptionKey: 'onboarding:questionnaire.orderingChannels.qr.description',
      },
      {
        id: 'counter',
        titleKey: 'onboarding:questionnaire.orderingChannels.counter.title',
        descriptionKey: 'onboarding:questionnaire.orderingChannels.counter.description',
      },
      {
        id: 'kiosk',
        titleKey: 'onboarding:questionnaire.orderingChannels.kiosk.title',
        descriptionKey: 'onboarding:questionnaire.orderingChannels.kiosk.description',
      },
      {
        id: 'delivery',
        titleKey: 'onboarding:questionnaire.orderingChannels.delivery.title',
        descriptionKey: 'onboarding:questionnaire.orderingChannels.delivery.description',
      },
    ];

    return (
      <div className="space-y-2">
        {channels.map((channel) => {
          const selected = form.orderingChannels.includes(channel.id);
          return (
            <button
              key={channel.id}
              type="button"
              onClick={() => toggleChannel(channel.id)}
              className={cn(
                'group flex w-full items-center justify-between border-b border-border/40 py-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40 focus-visible:ring-offset-0',
                selected ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span className="space-y-2">
                <span className="block text-lg font-medium">
                  {t(channel.titleKey, {
                    defaultValue:
                      channel.id === 'qr'
                        ? 'QR table ordering'
                        : channel.id === 'counter'
                          ? 'Counter service'
                          : channel.id === 'kiosk'
                            ? 'Self-serve kiosk'
                            : 'Delivery & pickup',
                  })}
                </span>
                <span className="block text-sm text-muted-foreground">
                  {t(channel.descriptionKey, {
                    defaultValue:
                      channel.id === 'qr'
                        ? 'Guests scan at the table to order and pay.'
                        : channel.id === 'counter'
                          ? 'Walk-up orders and cashier flows stay in sync.'
                          : channel.id === 'kiosk'
                            ? 'Touch-friendly kiosk mode for self-service lines.'
                            : 'Coordinate takeout, curbside, and delivery handoffs.',
                  })}
                </span>
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full border border-border/70 transition group-hover:border-foreground/70',
                  selected ? 'border-foreground bg-foreground text-background' : undefined,
                )}
              >
                {selected ? <Check className="h-3.5 w-3.5 stroke-[3]" /> : null}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderPrepStyle = () => {
    const styles: Array<{ id: OnboardingPrepStyle; labelKey: string; descriptionKey: string }> = [
      {
        id: 'fast_casual',
        labelKey: 'onboarding:questionnaire.prepStyle.fastCasual.title',
        descriptionKey: 'onboarding:questionnaire.prepStyle.fastCasual.description',
      },
      {
        id: 'full_service',
        labelKey: 'onboarding:questionnaire.prepStyle.fullService.title',
        descriptionKey: 'onboarding:questionnaire.prepStyle.fullService.description',
      },
      {
        id: 'coffee_bar',
        labelKey: 'onboarding:questionnaire.prepStyle.coffeeBar.title',
        descriptionKey: 'onboarding:questionnaire.prepStyle.coffeeBar.description',
      },
    ];

    return (
      <div className="space-y-2">
        {styles.map((style) => {
          const selected = form.prepStyle === style.id;
          return (
            <button
              key={style.id}
              type="button"
              onClick={() => setPrepStyle(style.id)}
              className={cn(
                'group flex w-full items-start justify-between border-b border-border/40 py-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40 focus-visible:ring-offset-0',
                selected ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span className="space-y-2">
                <span className="block text-lg font-medium">
                  {t(style.labelKey, {
                    defaultValue:
                      style.id === 'fast_casual'
                        ? 'Fast casual'
                        : style.id === 'full_service'
                          ? 'Full service'
                          : 'Coffee / beverage bar',
                  })}
                </span>
                <span className="block text-sm text-muted-foreground">
                  {t(style.descriptionKey, {
                    defaultValue:
                      style.id === 'fast_casual'
                        ? 'Higher throughput with shorter prep windows.'
                        : style.id === 'full_service'
                          ? 'Coordinated pacing for coursed dining.'
                          : 'Optimized for beverage-forward programs.',
                  })}
                </span>
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  'mt-2 h-3 w-3 rounded-full border border-border/70 transition group-hover:border-foreground/70',
                  selected ? 'border-foreground bg-foreground' : undefined,
                )}
              />
            </button>
          );
        })}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep?.id) {
      case 'venueName':
        return (
          <div className="mx-auto max-w-xl space-y-8">
            <LabelledHeadline
              title={t('onboarding:questionnaire.venueName.title', {
                defaultValue: "What's your restaurant or brand name?",
              })}
              description={t('onboarding:questionnaire.venueName.description', {
                defaultValue: 'We will use this in your dashboard header and signage exports.',
              })}
            />
            <Input
              autoFocus
              value={form.venueName}
              onChange={(event) => setVenueName(event.target.value)}
              placeholder={t('onboarding:questionnaire.venueName.placeholder', {
                defaultValue: 'Riverfront Social Club',
              })}
              onKeyDown={handleEnterAdvance}
              className={inlineInputClass}
            />
          </div>
        );
      case 'conceptType':
        return (
          <div className="mx-auto max-w-xl space-y-8">
            <LabelledHeadline
              title={t('onboarding:questionnaire.conceptType.title', {
                defaultValue: 'Where will guests experience your concept?',
              })}
              description={t('onboarding:questionnaire.conceptType.description', {
                defaultValue: 'We tune floor maps and location metadata for your footprint.',
              })}
            />
            {renderConceptTypeOptions()}
          </div>
        );
      case 'seatingIndoor':
        return (
          <div className="mx-auto max-w-xl space-y-8">
            <LabelledHeadline
              title={t('onboarding:questionnaire.seating.indoor.title', {
                defaultValue: 'How many indoor tables do you need?',
              })}
              description={t('onboarding:questionnaire.seating.indoor.description', {
                defaultValue: 'We will create numbered tables and QR codes automatically.',
              })}
            />
            <Input
              type="number"
              min={0}
              max={200}
              value={form.seating.indoor}
              onChange={(event) => updateSeating('indoor', event.target.value)}
              onKeyDown={handleEnterAdvance}
              className={inlineInputClass}
            />
          </div>
        );
      case 'seatingPatio':
        return (
          <div className="mx-auto max-w-xl space-y-8">
            <LabelledHeadline
              title={t('onboarding:questionnaire.seating.patio.title', {
                defaultValue: 'Outdoor tables or patio seats?',
              })}
              description={t('onboarding:questionnaire.seating.patio.description', {
                defaultValue: 'Patio zones inherit weather-safe routing and staging rules.',
              })}
            />
            <Input
              type="number"
              min={0}
              max={200}
              value={form.seating.patio}
              onChange={(event) => updateSeating('patio', event.target.value)}
              onKeyDown={handleEnterAdvance}
              className={inlineInputClass}
            />
          </div>
        );
      case 'seatingBar':
        return (
          <div className="mx-auto max-w-xl space-y-8">
            <LabelledHeadline
              title={t('onboarding:questionnaire.seating.bar.title', {
                defaultValue: 'Bar seats or standing rails?',
              })}
              description={t('onboarding:questionnaire.seating.bar.description', {
                defaultValue: 'Bar zones get faster throttles and separate service pacing.',
              })}
            />
            <Input
              type="number"
              min={0}
              max={200}
              value={form.seating.bar}
              onChange={(event) => updateSeating('bar', event.target.value)}
              onKeyDown={handleEnterAdvance}
              className={inlineInputClass}
            />
          </div>
        );
      case 'orderingChannels':
        return (
          <div className="mx-auto max-w-xl space-y-8">
            <LabelledHeadline
              title={t('onboarding:questionnaire.orderingChannels.title', {
                defaultValue: 'Which ordering channels launch on day one?',
              })}
              description={t('onboarding:questionnaire.orderingChannels.description', {
                defaultValue: 'We preload the right surfaces, notifications, and staff prompts.',
              })}
            />
            {renderOrderingChannels()}
          </div>
        );
      case 'prepStyle':
        return (
          <div className="mx-auto max-w-xl space-y-8">
            <LabelledHeadline
              title={t('onboarding:questionnaire.prepStyle.title', {
                defaultValue: 'How would you describe your prep pace?',
              })}
              description={t('onboarding:questionnaire.prepStyle.description', {
                defaultValue: 'This helps us fine-tune kitchen station throttles and ticket timers.',
              })}
            />
            {renderPrepStyle()}
          </div>
        );
      case 'notes':
        return (
          <div className="mx-auto max-w-xl space-y-8">
            <LabelledHeadline
              title={t('onboarding:questionnaire.notes.title', {
                defaultValue: 'Anything else we should configure?',
              })}
              description={t('onboarding:questionnaire.notes.description', {
                defaultValue: 'Optional-share opening goals, staffing notes, or anything unique.',
              })}
            />
            <Textarea
              rows={4}
              value={form.notes ?? ''}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={t('onboarding:questionnaire.notes.placeholder', {
                defaultValue: 'Example: Build a patio zone for 5 high-tops and enable curbside staging.',
              })}
              onKeyDown={handleEnterAdvance}
              className={inlineTextareaClass}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const currentStepTitle = t(`onboarding:questionnaire.stepTitles.${currentStep?.id ?? 'venueName'}`, {
    defaultValue: t('onboarding:questionnaire.defaultStepTitle', {
      defaultValue: 'Configuration',
    }),
  });
  const brandLabel = 'Braise';

  if (!flow.hydrated) {
    return null;
  }

  if (!flow.hasSignupContext || !flow.paymentComplete) {
    return null;
  }

  if (preparing && !showLaunchScreen) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-muted px-6 py-16">
        <div className="w-full max-w-md space-y-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-border/60 bg-background/80">
            <Loader2 className="h-8 w-8 animate-spin text-foreground" />
          </div>
          <p className="text-lg font-semibold text-foreground">
            {t('onboarding:questionnaire.loading.title', {
              defaultValue: 'Preparing your dashboard',
            })}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('onboarding:questionnaire.loading.description', {
              defaultValue: 'We are generating tables, menus, and workflows based on your answers.',
            })}
          </p>
        </div>
      </div>
    );
  }

  const isLastStep = activeIndex === steps.length - 1;
  const nextLabel = t('onboarding:questionnaire.actions.next', { defaultValue: 'Next' });
  const forwardDisabled = isLastStep ? !canSubmit || isPending : !canAdvance || isPending;

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(130deg,hsl(var(--background)) 0%,hsl(var(--background)/0.94) 38%,hsl(var(--foreground)/0.08) 100%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(160%_140%_at_78%_-10%,hsl(var(--foreground)/0.12),transparent)]" />
      <div className="relative z-10 flex flex-1 flex-col">
        <header className="px-6 pt-10">
          <div className="relative mx-auto w-full max-w-4xl">
            <span className="absolute left-0 top-0 text-base font-semibold uppercase tracking-[0.38em] text-foreground">
              {brandLabel}
            </span>
            <div className="absolute right-0 top-0 flex items-center gap-2 rounded-full border border-border/40 bg-background/85 px-3 py-1 text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground/80 shadow-sm">
              <Languages className="h-4 w-4 text-muted-foreground/80" aria-hidden="true" />
              <div className="flex items-center gap-1">
                {languageOptions.map((option) => {
                  const active = option.code === resolvedLanguage;
                  return (
                    <button
                      key={option.code}
                      type="button"
                      onClick={() => handleLanguageSelect(option.code)}
                      aria-pressed={active}
                      className={cn(
                        'rounded-full px-2 py-1 text-[0.7rem] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-foreground/40',
                        active
                          ? 'bg-foreground text-background'
                          : 'text-muted-foreground/80 hover:text-foreground',
                      )}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-4 pt-12 text-center">
              <Progress
                value={progressValue}
                className="h-[3px] w-full max-w-md overflow-hidden rounded-full bg-foreground/10 [&>div]:bg-foreground"
              />
              <div className="flex w-full max-w-md items-center justify-between text-[0.65rem] uppercase tracking-[0.28em] text-muted-foreground/80">
                <span>
                  {t('onboarding:questionnaire.progress', {
                    defaultValue: 'Question {{current}} of {{total}}',
                    current: activeIndex + 1,
                    total: steps.length,
                  })}
                </span>
                <span>
                  {t('onboarding:questionnaire.timer', {
                    defaultValue: '{{minutes}}:{{seconds}} remaining',
                    minutes,
                    seconds,
                  })}
                </span>
              </div>
            </div>
          </div>
        </header>
        <main className="flex flex-1 items-center px-6 py-12">
          {showLaunchScreen ? (
            <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center gap-8 text-center md:gap-12">
              <div className="space-y-4">
                <h2 className="text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
                  {t('onboarding:questionnaire.launch.title', {
                    defaultValue: 'Launch Your Next Gen Kitchen',
                  })}
                </h2>
                <p className="text-sm text-muted-foreground md:text-base">
                  {t('onboarding:questionnaire.launch.subtitle', {
                    defaultValue:
                      'We are wiring stations, syncing QR tables, and preparing dashboards. Hang tight for just a moment.',
                  })}
                </p>
              </div>
              <AnimatedGenerateButton
                labelIdle={t('onboarding:questionnaire.launch.buttonIdle', {
                  defaultValue: 'Launch Your Next Gen Kitchen',
                })}
                labelActive={t('onboarding:questionnaire.launch.buttonActive', {
                  defaultValue: 'Launching…',
                })}
                generating={launchButtonActive || isPending}
                highlightHueDeg={210}
                disabled={launchButtonActive || isPending}
                className="scale-110 md:scale-125"
                ariaLabel={t('onboarding:questionnaire.launch.buttonIdle', {
                  defaultValue: 'Launch Your Next Gen Kitchen',
                })}
              />
              {submitError ? (
                <p className="text-sm font-medium text-destructive">{submitError}</p>
              ) : null}
            </div>
          ) : (
            <div className="mx-auto w-full max-w-3xl space-y-12">
              <div className="space-y-6 md:space-y-8">
                <div className="mx-auto max-w-xl">
                  <span className="text-[0.65rem] font-semibold uppercase tracking-[0.32em] text-muted-foreground/80">
                    {currentStepTitle}
                  </span>
                </div>
                {renderStepContent()}
              </div>
              {submitError ? (
                <p className="text-sm font-medium text-destructive">{submitError}</p>
              ) : null}
            </div>
          )}
        </main>
        {!showLaunchScreen ? (
          <footer className="px-6 pb-12">
            <div className="mx-auto flex w-full max-w-3xl items-center justify-between">
              <button
                type="button"
                onClick={handleBack}
                disabled={activeIndex === 0}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground/80 transition hover:-translate-x-1 hover:text-foreground disabled:pointer-events-none disabled:text-muted-foreground/50"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('onboarding:questionnaire.actions.back', { defaultValue: 'Back' })}
              </button>
              <div className="flex items-center gap-6">
                <span className="text-xs uppercase tracking-[0.24em] text-muted-foreground/70">
                  {t('onboarding:questionnaire.hint', { defaultValue: 'Press Enter to continue' })}
                </span>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={forwardDisabled}
                  className="group flex items-center gap-2 text-base font-medium text-foreground transition hover:translate-x-1 disabled:pointer-events-none disabled:text-muted-foreground/50"
                >
                  <span>{nextLabel}</span>
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </footer>
        ) : null}
      </div>
    </div>
  );
};

const LabelledHeadline = ({
  title,
  description,
}: {
  title: string;
  description?: string;
}) => (
  <div className="mx-auto max-w-xl space-y-4">
    <h2 className="text-balance text-3xl font-semibold leading-tight text-foreground md:text-5xl">{title}</h2>
    {description ? (
      <p className="text-balance text-base text-muted-foreground md:text-lg">{description}</p>
    ) : null}
  </div>
);

export default QuestionnairePage;
